import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Plus, Edit2, Trash2, Save, X, 
  MessageSquare, Info, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';
import { toast } from 'sonner';

const TEMPLATE_TYPES = [
  { value: 'referral_thanks', label: 'Referral Thank You', icon: '🙏' },
  { value: 'patient_update', label: 'Patient Update', icon: '📋' },
  { value: 'discharge_notification', label: 'Discharge Notification', icon: '🏥' },
  { value: 'custom', label: 'Custom Template', icon: '✏️' }
];

export default function MessageTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [availableVariables, setAvailableVariables] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    template_type: 'referral_thanks',
    content: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/message-templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchVariables = async (templateType) => {
    try {
      const response = await api.get(`/message-templates/available-variables/${templateType}`);
      setAvailableVariables(response.data.variables || []);
    } catch (error) {
      console.error('Failed to fetch variables:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      template_type: 'referral_thanks',
      content: '',
      description: '',
      is_active: true
    });
    setEditingTemplate(null);
    setAvailableVariables([]);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
    fetchVariables('referral_thanks');
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type,
      content: template.content,
      description: template.description || '',
      is_active: template.is_active
    });
    setShowModal(true);
    fetchVariables(template.template_type);
  };

  const handleTemplateTypeChange = (type) => {
    setFormData({ ...formData, template_type: type });
    fetchVariables(type);
  };

  const insertVariable = (variable) => {
    setFormData({
      ...formData,
      content: formData.content + variable
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingTemplate) {
        await api.put(`/message-templates/${editingTemplate.id}`, formData);
        toast.success('Template updated successfully');
      } else {
        await api.post('/message-templates', formData);
        toast.success('Template created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;
    
    try {
      await api.delete(`/message-templates/${template.id}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const toggleActive = async (template) => {
    try {
      await api.put(`/message-templates/${template.id}`, {
        is_active: !template.is_active
      });
      fetchTemplates();
      toast.success(`Template ${template.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const getTypeInfo = (type) => TEMPLATE_TYPES.find(t => t.value === type) || TEMPLATE_TYPES[3];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Message Templates</h1>
          <p className="text-slate-600">Configure WhatsApp message templates for referring doctors</p>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-template-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-700 mb-1">About Message Templates</h3>
              <p className="text-sm text-blue-700">
                Templates use placeholders like <code className="bg-blue-500/20 px-1 rounded">{'{{Doctor_Name}}'}</code> that are automatically replaced with actual values when sending messages. The "Referral Thank You" template is sent automatically when a visit is created with a referring doctor.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Templates Yet</h3>
            <p className="text-slate-600 mb-4">Create your first message template</p>
            <Button onClick={openCreateModal} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {templates.map((template) => {
            const typeInfo = getTypeInfo(template.template_type);
            return (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className={`bg-white border-slate-200 shadow-sm ${!template.is_active && 'opacity-60'}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{typeInfo.icon}</span>
                        <div>
                          <CardTitle className="text-slate-900 text-lg">{template.name}</CardTitle>
                          <p className="text-sm text-slate-600">{typeInfo.label}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={template.is_active}
                          onCheckedChange={() => toggleActive(template)}
                        />
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(template)}
                          className="text-slate-500 hover:text-slate-900">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(template)}
                          className="text-slate-500 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-slate-100 rounded-lg p-3 mb-3">
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{template.content}</p>
                    </div>
                    {template.description && (
                      <p className="text-xs text-slate-600">{template.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <Badge variant={template.is_active ? 'default' : 'secondary'}
                        className={template.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                        {template.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-emerald-400" />
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-slate-300">Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="e.g., Referral Thank You"
                  required
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Template Type *</Label>
                <Select value={formData.template_type} onValueChange={handleTemplateTypeChange}>
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-slate-300">Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                />
              </div>
            </div>

            {/* Available Variables */}
            {availableVariables.length > 0 && (
              <div>
                <Label className="text-slate-300 mb-2 block">Available Variables (click to insert)</Label>
                <div className="flex flex-wrap gap-2">
                  {availableVariables.map((v, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => insertVariable(v.name)}
                      className="border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20"
                    >
                      {v.name}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Click a variable to insert it at the end of your message
                </p>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Message Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                className="bg-slate-700/50 border-slate-600 text-white font-mono text-sm"
                placeholder="Dear Dr. {{Doctor_Name}}, thank you for referring..."
                rows={6}
                required
                data-testid="template-content-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Character count: {formData.content.length}/1000
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Description (internal use)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-slate-700/50 border-slate-600 text-white"
                placeholder="When this template is used..."
              />
            </div>

            {/* Preview */}
            <div>
              <Label className="text-slate-300 mb-2 block">Preview</Label>
              <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4">
                <p className="text-sm text-emerald-100 whitespace-pre-wrap">
                  {formData.content
                    .replace(/\{\{Doctor_Name\}\}/g, 'Dr. John Smith')
                    .replace(/\{\{Patient_Name\}\}/g, 'Jane Doe')
                    .replace(/\{\{Hospital_Name\}\}/g, 'Zesta Healthcare')
                    .replace(/\{\{Admission_Date\}\}/g, new Date().toLocaleDateString())
                    .replace(/\{\{Discharge_Date\}\}/g, new Date().toLocaleDateString())
                    || 'Your message preview will appear here...'}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}
                className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : (editingTemplate ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
